import { useCallback, useId } from 'react';
import type { DropdownProps } from './Dropdown.typed';
import * as Styled from './Dropdown.styled';
import Icon from '../Icon/Icon';
import { useProvidedStateOrCreate } from 'hooks';
import {
  FloatingPortal,
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
  useDismiss,
  useInteractions,
  FloatingFocusManager,
} from '@floating-ui/react';

// TODO: Memoize children for when window resizes
// TODO: Should children have to manually close dropdown on click e.g. Menu?
const Dropdown = (props: DropdownProps) => {
  const {
    children,
    alignX = 'left',
    alignY = 'top',
    isOpen,
    renderAnchor,
    text,
    disableFocusTrap = false,
  } = props;

  const toggleState = useCallback(() => {
    !isOpen && !!props.onOpen && props.onOpen();
    isOpen && !!props.onClose && props.onClose();
  }, [isOpen, props]);

  const [combinedOpenState, setCombinedOpenState] = useProvidedStateOrCreate(
    isOpen,
    typeof isOpen !== 'undefined' ? toggleState : undefined,
    false,
  );
  const onOpen = useCallback(
    () => setCombinedOpenState(true),
    [setCombinedOpenState],
  );
  const onClose = useCallback(() => {
    setCombinedOpenState(false);
  }, [setCombinedOpenState]);
  const id = useId();
  const aria = {
    'aria-expanded': combinedOpenState,
    'aria-controls': id,
  };

  const handleOnClick = () => {
    combinedOpenState ? onClose() : onOpen();
  };

  // Map alignX/alignY to floating-ui placement
  const getPlacement = () => {
    const side = alignY === 'top' ? 'bottom' : 'top';
    const alignment = alignX === 'left' ? 'start' : 'end';
    return `${side}-${alignment}` as const;
  };

  const { refs, floatingStyles, context } = useFloating({
    open: combinedOpenState,
    onOpenChange: (open) => {
      if (open) {
        onOpen();
      } else {
        onClose();
      }
    },
    placement: getPlacement(),
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(0), // 0px gap between button and dropdown
      flip(), // Flip to opposite side if not enough space
      shift({ padding: 8 }), // Shift along the axis to stay in viewport
    ],
  });

  const dismiss = useDismiss(context, {
    outsidePress: true,
    escapeKey: true,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  const referenceProps = getReferenceProps({
    ref: refs.setReference,
    onClick: handleOnClick,
    ...aria,
  });

  const anchor = renderAnchor
    ? renderAnchor({
        ...referenceProps,
        ref: refs.setReference,
        onClick: handleOnClick,
        isOpen: combinedOpenState,
      })
    : null;

  return (
    <>
      {/* Only render default anchor if no custom anchor is provided */}
      {anchor ?? (
        <Styled.Button
          type="button"
          isOpen={combinedOpenState}
          {...getReferenceProps({
            ref: refs.setReference,
            onClick: handleOnClick,
            ...aria,
          })}
        >
          {text}
          <Icon name="ChevronDownOutline" size="xs" />
        </Styled.Button>
      )}
      {combinedOpenState && (
        <FloatingPortal>
          <FloatingFocusManager
            context={context}
            modal={false}
            disabled={disableFocusTrap}
          >
            <Styled.Dropdown
              {...getFloatingProps({
                ref: refs.setFloating,
                style: floatingStyles,
              })}
              role="listbox"
              id={id}
            >
              {children({ close: onClose })}
            </Styled.Dropdown>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
};

export default Dropdown;
